import React, { useEffect, useState, useRef } from "react";
import styled from "styled-components";
import { Link } from "react-router-dom";

const AboutPageWrapper = styled.div`
  width: 100%;
`;

const HeroSection = styled.section`
  position: relative;
  color: white;
  height: 23vh;
  padding: 80px 0;
  text-align: center;
  display: flex;
  align-items: center;
  overflow: hidden;
  background: linear-gradient(135deg, #ff6720 0%, #ff8c42 100%); /* Desktop gradient */

  &::before {
    content: '';
    position: absolute;
    top: 0;
    right: 0;
    width: 60%;
    height: 100%;
    background: 
      url('https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/About.jpg')
        center/cover no-repeat;
    clip-path: polygon(15% 0%, 100% 0%, 100% 100%, 0% 100%);
    z-index: 1;
  }

  /* Responsive: Remove gradient, show only image + text */
  @media (max-width: 768px) {
    padding: 60px 0;
    height: 15vh;
    background: transparent;
    background-image: url('https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/About.jpg');
    background-size: cover;
    background-position: center;
    background-repeat: no-repeat;

    &::before {
      display: none;
    }

    &::after {
      content: '';
      position: absolute;
      top: 0; left: 0; right: 0; bottom: 0;
      background: rgba(0, 0, 0, 0.5);
      z-index: 1;
    }

    div.container {
      text-align: center;
      width: 100%;
      padding: 0 20px;
      position: relative;
      z-index: 2;
    }
  }

  div.container {
    max-width: 1300px;
    border-radius: 10px;
    text-align: left;
    display: inline-block;
    margin: 0 auto;
    position: relative;
    z-index: 2;
  }
`;

const HeroTitle = styled.h1`
  font-size: 48px;
  font-weight: 900;
  margin-bottom: 20px;
  line-height: 1.2;

  @media (max-width: 768px) {
    font-size: 36px;
  }

  @media (max-width: 480px) {
    font-size: 28px;
  }
`;

const ContentSection = styled.section`
  padding: 80px 0;

  div.container {
    max-width: 1300px;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  &.bg-light {
    background: #ffffffff;
  }
`;

const SectionTitle = styled.h2`
  font-size: 36px;
  font-weight: bold;
  color: #333;
  margin-bottom: 30px;
  text-align: center;

  @media (max-width: 768px) {
    font-size: 28px;
    margin-bottom: 20px;
  }

  @media (max-width: 480px) {
    font-size: 24px;
  }
`;

// CORRECT ORDER — MUST BE LIKE THIS
const ContentImage = styled.div`
  width: 100%;
  height: 400px;
  border-radius: 12px;
  overflow: hidden;

  img {
    width: 100%;
    height: 100%;
    object-fit: contain;
  }

  @media (max-width: 768px) {
    height: auto;
    padding: 20px 0px;
  }
`;

const ContentText = styled.div`
  h3 {
    font-size: 28px;
    font-weight: bold;
    color: #333;
    margin-bottom: 20px;
    line-height: 1.3;
  }

  p {
    font-size: 18px;
    color: #666;
    line-height: 1.6;
    margin-bottom: 20px;
    word-spacing: .05em;
    letter-spacing: 0.05em;

    &:last-child {
      margin-bottom: 0;
    }

    @media (max-width: 768px) {
      font-size: 15px;
    }
  }
`;

// NOW SAFE TO REFERENCE ContentImage
const ContentGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 60px;
  align-items: center;

  @media (max-width: 1024px) {
    gap: 40px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    gap: 20px;
  }

  /* MOBILE: Split text using display: contents */
  &.reverse-on-mobile {
    @media (max-width: 768px) {
      .mobile-split-text {
        display: contents;
      }

      /* Title first */
      > .mobile-split-text > h3 {
        order: 1;
        text-align: center;
        font-size: 28px;
        margin: 0 0 20px 0;
        font-weight: bold;
      }

      /* Image second */
      > ${ContentImage} {
        order: 2;
      }

      /* Paragraphs last */
      > .mobile-split-text > p {
        order: 3;
        margin-bottom: 16px;
        text-align: left;
        &:last-child {
          margin-bottom: 0;
        }
      }
    }
  }

  &.reverse {
    @media (min-width: 769px) {
      direction: rtl;
      > * {
        direction: ltr;
      }
    }
  }
`;

const CTASection = styled.section`
  background: #333;
  color: white;
  padding: 80px 0;
  text-align: center;

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  h2 {
    font-size: 36px;
    font-weight: bold;
    margin-bottom: 20px;

    @media (max-width: 768px) {
      font-size: 28px;
    }
  }

  p {
    font-size: 18px;
    margin-bottom: 30px;
    opacity: 0.9;

    @media (max-width: 768px) {
      font-size: 16px;
    }
  }
`;

const CTAButton = styled(Link)`
  display: inline-block;
  background: #ff6720;
  color: white;
  padding: 16px 32px;
  border-radius: 30px;
  font-weight: bold;
  font-size: 16px;
  text-decoration: none;
  transition: all 0.3s ease;

  &:hover {
    background: #e55a1c;
    transform: translateY(-2px);
  }

  @media (max-width: 480px) {
    padding: 14px 28px;
    font-size: 15px;
  }
`;

const ParallaxSection = styled.section`
  position: relative;
  min-height: 300px;
  display: flex;
  align-items: center;
  justify-content: center;
  overflow: hidden;
  background-attachment: fixed;
  background-position: center;
  background-repeat: no-repeat;
  background-size: cover;
  background-image: url('https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/holdingintreewithstihlchainsaw.jpg');

  &::before {
    content: '';
    position: absolute;
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    background: rgba(0, 0, 0, 0.4);
    z-index: 1;
  }

  @media (max-width: 768px) {
    background-attachment: scroll;
    min-height: 60vh;
  }

  @media (max-width: 480px) {
    min-height: 5vh;
    background-attachment: fixed;
    background-position: center;
    background-repeat: no-repeat;
  }
`;

const ParallaxContent = styled.div`
  position: relative;
  z-index: 2;
  color: white;
  display: flex;
  height: 100%;
  flex-direction: column; /* Ensure children stack vertically */
  align-items: flex-start; /* Align flex items to the left */
  justify-content: flex-start; /* Align content to the top */
  padding: 60px 20px;
  max-width: 1300px;
  width: 100%; /* Ensure the container spans the full width */
  margin: 0 auto;
  text-align: left; /* Ensure text alignment is left */
`;

const ParallaxStatsGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(4, 1fr);
  gap: 40px;
  width: 100%;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 30px;
  }

  @media (max-width: 768px) {
    grid-template-columns: repeat(2, 1fr);
    gap: 20px;
    margin-top: 40px;
  }
`;

const ParallaxStatItem = styled.div`
  text-align: center;

  h3 {
    font-size: 56px;
    font-weight: 900;
    margin-bottom: 10px;
    line-height: 1;
    color: #ff6720;
    text-shadow: 2px 2px 8px rgba(0, 0, 0, 0.5);

    @media (max-width: 768px) {
      font-size: 42px;
    }

    @media (max-width: 480px) {
      font-size: 32px;
    }
  }

  p {
    font-size: 16px;
    margin: 0;
    font-weight: 600;
    text-shadow: 1px 1px 4px rgba(0, 0, 0, 0.5);

    @media (max-width: 768px) {
      font-size: 14px;
    }
  }
`;

const DiscoverySection = styled.section`
  padding: 80px 0;
  background: #ffffffff;

  div.container {
    max-width: 1300px;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }
`;

const DiscoveryCarousel = styled.div`
  display: none;

  @media (max-width: 768px) {
    display: block;
    position: relative;
    width: 100%;
    overflow: hidden;
  }
`;

const CarouselItem = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  opacity: ${props => (props.$isActive ? 1 : 0)}; /* Only opacity transition */
  transition: opacity 0.5s ease; /* Remove transform transition */
  position: ${props => (props.$isActive ? 'relative' : 'absolute')};
  width: 100%;
  top: 0;
  left: 0;
`;

const CarouselImage = styled.div`
  width: 100%;
  height: 300px;
  border-radius: 8px;
  overflow: hidden;
  background: #e0e0e0;
  position: relative;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    /* Remove transition and opacity animation on mobile */
    @media (max-width: 768px) {
      transition: none;
      opacity: 1 !important;
    }
  }

  /* Disable animation class on mobile */
  @media (max-width: 768px) {
    &.animate {
      img {
        opacity: 1;
        animation: none;
      }
    }
  }

  &.animate {
    img {
      opacity: 0;
    }

    img {
      animation: none;
    }
  }

  @keyframes fadeIn {
    from {
      opacity: 0;
    }
    to {
      opacity: 1;
    }
  }
`;

const CarouselContent = styled.div`
  background: white;
  padding: 0px;
  border-radius: 8px;
  text-align: left;
  width: 100%;
  height: 120px;
  display: flex;
  justify-content: space-between;
  align-items: start;
  gap: 20px;

  h3 {
    font-size: 20px;
    font-weight: bold;
    color: #333;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06rem;
  }

  p {
    font-size: 15px;
    color: #494949;
    line-height: 1.6;
    margin: 0;
  }
`;

const CarouselTextContent = styled.div`
  @media (max-width: 768px) {
    animation: ${props => props.$isActive ? 'slideInRight 0.6s ease-out' : 'none'};
  }

  @keyframes slideInRight {
    from {
      opacity: 0;
      transform: translateX(50px);
    }
    to {
      opacity: 1;
      transform: translateX(0);
    }
  }
`;

const CarouselIndicator = styled.div`
  background: transparent;
  color: #333;
  padding: 5px 10px;
  border-radius: 5px;
  font-size: 16px;
  font-weight: 700;
  white-space: nowrap;
  flex-shrink: 0; /* Prevent the indicator from shrinking */
`;

const CarouselDots = styled.div`
  position: relative;
  bottom: 10px;
  justify-content: center;
  display: flex;
  gap: 10px;
`;

const CarouselDot = styled.div`
  width: 10px;
  height: 10px;
  background: ${props => (props.$isActive ? '#333' : '#ccc')};
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.3s ease;
`;

const DiscoveryGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr 1fr;
  gap: 40px;
  align-items: stretch;

  @media (max-width: 968px) {
    grid-template-columns: 1fr;
    gap: 30px;
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const DiscoveryImageContainer = styled.div`
  width: 100%;
  height: auto;
  border-radius: 8px;
  overflow: hidden;
  background: #e0e0e0;

  img {
    width: 100%;
    height: 100%;
    object-fit: cover;
    transition: opacity 0.5s ease;
  }

  @media (max-width: 968px) {
    height: 400px;
  }
`;

const DiscoveryCardsContainer = styled.div`
  display: flex;
  flex-direction: column;
  gap: 0px;
`;

const DiscoveryCard = styled.div`
  background: white;
  padding: 30px;
  cursor: pointer;
  transition: all 0.3s ease;
  position: relative;
  overflow: hidden;
  z-index: 1;
  border-bottom: 1px solid #333;

  &::before {
    content: '';
    position: absolute;
    top: -100%;
    left: 0;
    width: 100%;
    height: 100%;
    background: #ededed;
    transition: top 0.4s ease;
    z-index: -1;
  }

  &.active::before {
    top: 0;
  }

  &.disabled::before {
    top: -100% !important;
  }

  &:hover::before {
    top: 0;
    border-bottom: 1px solid #333;
  }

  &.active {
    border-color: #ffffff;
  }

  &.disabled {
    border-bottom: 1px solid #333;
  }

  h3 {
    font-size: 24px;
    font-weight: bold;
    color: #333;
    margin-bottom: 10px;
    text-transform: uppercase;
    letter-spacing: 0.06rem;
    word-spacing: 0.03rem;
    position: relative;
    transition: color 0.3s ease 0.1s;

    @media (max-width: 768px) {
      font-size: 20px;
    }
  }

  p {
    font-size: 15px;
    color: #494949ff;
    font-weight: 400;
    line-height: 1.6;
    margin: 0;
    letter-spacing: 0.02rem;
    word-spacing: 0.04rem;
    position: relative;
    transition: color 0.3s ease 0.1s;
  }

  &:hover h3,
  &.active h3 {
    color: #333;
  }

  &:hover p,
  &.active p {
    color: #333;
  }

  &.disabled h3 {
    color: #333;
  }

  &.disabled p {
    color: #666;
  }
`;

const GridSection = styled.section`
  padding: 0px 0;
  background: #ffffffff;

  div.container {
    max-width: 1300px;
  }

  @media (max-width: 768px) {
    padding: 60px 0;
  }

  @media (max-width: 480px) {
    padding: 30px 0;
  }
`;

const SixGridContainer = styled.div`
  display: grid;
  grid-template-columns: repeat(3, 1fr);
  column-gap: 0;
  row-gap: 60px;

  @media (max-width: 1024px) {
    grid-template-columns: repeat(2, 1fr);
    row-gap: 50px;
  }

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
    row-gap: 40px;
  }
`;

const GridItem = styled.div`
  background: white;
  padding: 10px 30px;
  // border-radius: 8px;
  text-align: start;
  // box-shadow: 0 4px 12px rgba(0, 0, 0, 0.1);
  transition: all 0.3s ease;
  position: relative;
  display: flex;
  flex-direction: column;
  justify-content: space-between;
  z-index: 1;

  @media (min-width: 769px) {
     &:nth-child(3n+1), &:nth-child(3n+2) {
       border-right: 1px solid #33333354;
     }
   }

   /* Mobile: Left border on ALL items */
   @media (max-width: 768px) {
     border-left: 1px solid #33333354;
     border-right: none !important; /* Override any desktop rule */
   }

  ${SixGridContainer}:hover & {
    filter: blur(2px);
    opacity: 0.7;
  }

  svg {
    width: 40px;
    height: 40px;
  }

  &:hover {
    filter: blur(0px) !important;
    opacity: 1 !important;
    transform: translateY(-5px) scale(1.02);
    box-shadow: 0 12px 25px rgba(0, 0, 0, 0.2);
    z-index: 2;
    border: none;
  }

  @media (max-width: 768px) {
    opacity: ${props => {
      const itemThreshold = (props.$index / 6) * 0.8 + 0.1;
      return props.$scrollProgress >= itemThreshold ? 1 : 0;
    }};
    transform: translateY(${props => {
      const itemThreshold = (props.$index / 6) * 0.8 + 0.1;
      return props.$scrollProgress >= itemThreshold ? '0px' : '80px';
    }});
    transition: opacity 0.5s ease, transform 0.5s ease;

    ${SixGridContainer}:hover & {
      filter: none;
    }

    &:hover {
      filter: blur(0px) !important;
      opacity: 1 !important;
      transform: none;
      box-shadow: none;
      border: none;
    }
  }

  h3 {
    font-size: 22px;
    font-weight: 600;
    color: #333;
    margin-bottom: 15px;
    transition: color 0.3s ease;
  }

  p {
    font-size: 16px;
    letter-spacing: 0.05em;
    color: #666;
    line-height: 1.5;
    margin: 0;
    transition: color 0.3s ease;
  }

  &:hover h3 {
    color: #ff6720;
  }

  &:hover p {
    color: #333;
  }
`;

const Svg1 = styled.div`
    margin-bottom: 20px
`

const discoveryData = [
  {
    title: "Quality First",
    description: "We provide only genuine STIHL products, backed by expert service to ensure peak performance.",
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/QualityFirst.jpg"
  },
  {
    title: "Customer-Centric",
    description: "Your needs drive us—whether you’re a farmer, landscaper, or government agency, we’re here to help.",
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/CustomerCentric.jpg"
  },
  {
    title: "Innovation & Expertise",
    description: "Our team stays ahead with the latest STIHL technology, offering solutions tailored to Kerala’s needs.",
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/InnovationExpertise.jpg"
  },
  {
    title: "Sustainability",
    description: "We promote eco-friendly practices, aligning with STIHL’s commitment to durable, efficient tools.",
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/Sustainability.jpg"
  },
  {
    title: "Community Focus",
    description: "As a Kerala-based business, we’re proud to support local growth and empowerment.",
    image: "https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/CommunityFocus.jpg"
  }
];


const useCountUp = (end, duration = 2000, shouldStart = false) => {
  const [count, setCount] = useState(0);
  
  useEffect(() => {
    if (!shouldStart) return;
    
    let startTime;
    let animationFrame;
    
    const animate = (currentTime) => {
      if (!startTime) startTime = currentTime;
      const progress = (currentTime - startTime) / duration;
      
      if (progress < 1) {
        setCount(Math.floor(end * progress));
        animationFrame = requestAnimationFrame(animate);
      } else {
        setCount(end);
      }
    };
    
    animationFrame = requestAnimationFrame(animate);
    
    return () => cancelAnimationFrame(animationFrame);
  }, [end, duration, shouldStart]);
  
  return count;
};

const AboutPage = () => {
  useEffect(() => {
    document.title = "About Us | XPLOD-GROUPS";
    return () => {
      document.title = "Home | XPLOD-GROUPS";
    };
  }, []);
  const [isParallaxVisible, setIsParallaxVisible] = useState(false);
  const parallaxRef = useRef(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting) {
          setIsParallaxVisible(true);
        }
      },
      { threshold: 0.3 }
    );

    if (parallaxRef.current) {
      observer.observe(parallaxRef.current);
    }

    return () => observer.disconnect();
  }, []);

  const years = useCountUp(25, 2000, isParallaxVisible);
  const countries = useCountUp(14, 2000, isParallaxVisible);
  const products = useCountUp(500, 2500, isParallaxVisible);
  const customers = useCountUp(1, 2000, isParallaxVisible);

  const [activeDiscovery, setActiveDiscovery] = useState(0);
  const [hoveredDiscovery, setHoveredDiscovery] = useState(null);
  const [isAnimating, setIsAnimating] = useState(false);
  const [dragStartX, setDragStartX] = useState(null);
  const carouselRef = useRef(null);

  const [gridScrollProgress, setGridScrollProgress] = useState(0);
  const gridRef = useRef(null);

  useEffect(() => {
    const handleScroll = () => {
      if (gridRef.current && window.innerWidth <= 768) {
        const rect = gridRef.current.getBoundingClientRect();
        const sectionHeight = rect.height;
        const windowHeight = window.innerHeight;
        
        const scrollProgress = Math.max(0, Math.min(1, 
          (windowHeight - rect.top) / (sectionHeight + windowHeight)
        ));
        
        setGridScrollProgress(scrollProgress);
      }
    };

    window.addEventListener('scroll', handleScroll);
    handleScroll();

    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  useEffect(() => {
    setActiveDiscovery(0);
  }, []);

  useEffect(() => {
    let slideInterval;
    const startSlide = () => {
      slideInterval = setInterval(() => {
        setActiveDiscovery(prev => (prev === discoveryData.length - 1 ? 0 : prev + 1));
      }, 5000); // Auto-slide every 5 seconds
    };

    startSlide();

    return () => clearInterval(slideInterval); // Cleanup interval on unmount
  }, [activeDiscovery]);

  useEffect(() => {
    // Trigger animation when activeDiscovery changes
    setIsAnimating(true);
    const timer = setTimeout(() => setIsAnimating(false), 500); // Match animation duration
    return () => clearTimeout(timer);
  }, [activeDiscovery]);

  const handleDragStart = (e) => {
    const target = e.target;
    // Check if the drag starts within CarouselContent
    if (target.closest('.carousel-content')) {
      setDragStartX(e.type === 'touchstart' ? e.touches[0].clientX : e.clientX);
    }
  };

  const handleDragMove = (e) => {
    if (dragStartX === null) return;

    const currentX = e.type === 'touchmove' ? e.touches[0].clientX : e.clientX;
    const diff = dragStartX - currentX;

    if (diff > 50) { // Threshold for swipe right
      setActiveDiscovery(prev => (prev === discoveryData.length - 1 ? 0 : prev + 1));
      setDragStartX(null);
    } else if (diff < -50) { // Threshold for swipe left
      setActiveDiscovery(prev => (prev === 0 ? discoveryData.length - 1 : prev - 1));
      setDragStartX(null);
    }
  };

  const handleDragEnd = () => {
    setDragStartX(null);
  };
;

  return (
    <AboutPageWrapper>
      <HeroSection>
        <div className="container">
          <HeroTitle>About XPLOD GROUPS</HeroTitle>
          <div style={{width: "80px", backgroundColor: "white", height: "10px", marginTop: "-10px"}}/>
        </div>
      </HeroSection>

      <ContentSection>
        <div className="container">
          <ContentGrid className="reverse-on-mobile">
            {/* TEXT BLOCK: Title + Paragraphs */}
            <ContentText className="mobile-split-text">
              <h3 style={{textAlign: "start"}}>Our Story</h3>
              <p>
                Since 2005, our journey as Kerala’s leading STIHL dealer has been rooted in empowering our customers with the world’s finest power tools.
                From our humble beginnings, we’ve grown to 14 branches across Kerala, serving over 100,000 customers—from farmers to landscapers to government institutions—with pride.
              </p>
              <p>
                Our Platinum Plus Medal from STIHL India is a testament to our excellence in sales and service, but it’s the trust we’ve built with our community that truly defines us.
              </p>
            </ContentText>

            {/* IMAGE BLOCK */}
            <ContentImage>
              <picture>
                <source
                  media="(max-width: 768px)"
                  srcSet="https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/xplodgroup-text-logo.png"
                />
                <img
                  src="https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/xplodgroup-logo.png"
                  alt="XPLOD GROUPS"
                  style={{ width: '100%', height: '100%', objectFit: 'contain' }}
                />
              </picture>
            </ContentImage>
          </ContentGrid>
        </div>
      </ContentSection>

      <ParallaxSection ref={parallaxRef}>
        <ParallaxContent>
          <ParallaxStatsGrid>
            <ParallaxStatItem>
              <h3>{years}+</h3>
              <p>Years of Trust</p>
            </ParallaxStatItem>
            <ParallaxStatItem>
              <h3>{countries}+</h3>
              <p>Countries Statewide</p>
            </ParallaxStatItem>
            <ParallaxStatItem>
              <h3>{products}+</h3>
              <p>Products in Our Lineup</p>
            </ParallaxStatItem>
            <ParallaxStatItem>
              <h3>{customers}M+</h3>
              <p>Satisfied Customers</p>
            </ParallaxStatItem>
          </ParallaxStatsGrid>
        </ParallaxContent>
      </ParallaxSection>

      <ContentSection>
        <div className="container">
          <ContentGrid className="reverse">
            <ContentText>
              <h3>Empowering Kerala’s Farmers</h3>
              <p>
                Kerala’s agricultural heart beats strong, and we’re proud to fuel it with STIHL’s world-class tools. From chainsaws and mist blowers to power tillers and brush cutters, our products are designed to tackle the diverse needs of Kerala’s farmers, from coconut groves to spice plantations. Through our partnership with India’s Sub-Mission on Agricultural Mechanization (SMAM), we’ve empowered thousands of farmers by providing access to these tools at subsidized rates, boosting productivity and sustainability. Our 14 branches offer hands-on guidance to ensure you choose the right equipment and maximize SMAM benefits, making advanced farming tools accessible across Kerala.
              </p>
            </ContentText>
            <ContentImage>
              <img
                src="https://r2.fivemanage.com/rTu19oDvgan9lzZXivPBw/STIHLMH710.jpg"
                alt="Product innovation"
              />
            </ContentImage>
          </ContentGrid>
        </div>
      </ContentSection>

      <GridSection ref={gridRef}>
        <div className="container">
          <SectionTitle>Why Choose Us</SectionTitle>
          <SixGridContainer>
            <GridItem $index={0} $scrollProgress={gridScrollProgress}>
              {/* <img src=""/> */}
              <Svg1>
                <svg width="20" height="17" viewBox="0 0 20 17" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M8.88895 8.49973C8.88895 8.59774 8.84993 8.69174 8.78047 8.76104C8.71101 8.83035 8.6168 8.86928 8.51858 8.86928H4.0741C3.97587 8.86928 3.88167 8.83035 3.81221 8.76104C3.74275 8.69174 3.70373 8.59774 3.70373 8.49973C3.70373 8.40172 3.74275 8.30772 3.81221 8.23841C3.88167 8.16911 3.97587 8.13017 4.0741 8.13017H8.51858C8.6168 8.13017 8.71101 8.16911 8.78047 8.23841C8.84993 8.30772 8.88895 8.40172 8.88895 8.49973ZM8.51858 5.17375H4.0741C3.97587 5.17375 3.88167 5.21268 3.81221 5.28199C3.74275 5.35129 3.70373 5.44529 3.70373 5.5433C3.70373 5.64131 3.74275 5.73531 3.81221 5.80461C3.88167 5.87392 3.97587 5.91285 4.0741 5.91285H8.51858C8.6168 5.91285 8.71101 5.87392 8.78047 5.80461C8.84993 5.73531 8.88895 5.64131 8.88895 5.5433C8.88895 5.44529 8.84993 5.35129 8.78047 5.28199C8.71101 5.21268 8.6168 5.17375 8.51858 5.17375ZM18.5186 10.693V16.6299C18.5187 16.6948 18.5017 16.7586 18.4692 16.8149C18.4368 16.8711 18.39 16.9179 18.3337 16.9504C18.2774 16.9829 18.2135 17 18.1484 17C18.0833 17 18.0194 16.9829 17.9631 16.9505L15.5557 15.5776L13.1482 16.9505C13.0919 16.9829 13.028 17 12.9629 17C12.8979 17 12.8339 16.9829 12.7776 16.9504C12.7213 16.9179 12.6746 16.8711 12.6421 16.8149C12.6096 16.7586 12.5926 16.6948 12.5927 16.6299V14.043H1.11112C0.816432 14.043 0.533814 13.9262 0.325439 13.7183C0.117064 13.5104 0 13.2284 0 12.9344V1.10866C0 0.814625 0.117064 0.532633 0.325439 0.324719C0.533814 0.116805 0.816432 0 1.11112 0H17.4075C17.7022 0 17.9848 0.116805 18.1932 0.324719C18.4016 0.532633 18.5186 0.814625 18.5186 1.10866V4.08911C18.9846 4.5044 19.3575 5.01321 19.6128 5.58222C19.868 6.15124 20 6.76764 20 7.39107C20 8.01449 19.868 8.63089 19.6128 9.19991C19.3575 9.76893 18.9846 10.2777 18.5186 10.693ZM15.5557 3.69553C14.8231 3.69553 14.1071 3.91227 13.498 4.31834C12.8889 4.72441 12.4142 5.30158 12.1339 5.97685C11.8535 6.65212 11.7802 7.39517 11.9231 8.11203C12.066 8.82889 12.4188 9.48737 12.9367 10.0042C13.4547 10.521 14.1146 10.873 14.8331 11.0156C15.5516 11.1582 16.2962 11.085 16.973 10.8053C17.6498 10.5256 18.2282 10.0519 18.6352 9.4442C19.0422 8.83647 19.2594 8.12197 19.2594 7.39107C19.2594 6.41095 18.8692 5.47098 18.1746 4.77793C17.48 4.08488 16.538 3.69553 15.5557 3.69553ZM12.5927 13.3039V10.693C11.7803 9.95966 11.268 8.95275 11.1541 7.86558C11.0402 6.77841 11.3328 5.68757 11.9756 4.80243C12.6184 3.9173 13.5662 3.30024 14.6371 3.06969C15.708 2.83914 16.8265 3.01135 17.7779 3.55326V1.10866C17.7779 1.01065 17.7389 0.916651 17.6694 0.847346C17.6 0.778042 17.5058 0.739107 17.4075 0.739107H1.11112C1.01289 0.739107 0.918684 0.778042 0.849225 0.847346C0.779767 0.916651 0.740746 1.01065 0.740746 1.10866V12.9344C0.740746 13.0324 0.779767 13.1264 0.849225 13.1957C0.918684 13.265 1.01289 13.3039 1.11112 13.3039H12.5927ZM17.7779 11.2289C17.1028 11.6198 16.3362 11.8257 15.5557 11.8257C14.7751 11.8257 14.0085 11.6198 13.3334 11.2289V15.9933L15.3705 14.8311C15.4268 14.7987 15.4906 14.7816 15.5557 14.7816C15.6207 14.7816 15.6845 14.7987 15.7408 14.8311L17.7779 15.9933V11.2289Z" fill="#404040"/>
                </svg>
              </Svg1>
              <h3>STIHL India's #1 Dealer</h3>
              <p>Awarded the Platinum Plus Medal for outstanding sales and service.</p>
            </GridItem>
            <GridItem $index={1} $scrollProgress={gridScrollProgress}>
              <Svg1>
                <svg width="41" height="44" viewBox="0 0 41 44" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M40.6397 21.3773L19.4637 0.258084C19.3403 0.134878 19.1831 0.0509578 19.0118 0.0169467C18.8406 -0.0170645 18.663 0.000362982 18.5017 0.0670228C18.3404 0.133683 18.2026 0.246577 18.1056 0.391415C18.0087 0.536253 17.957 0.70652 17.9572 0.880662V10.5603H13.5455C12.8435 10.5603 10.8984 10.5596 10.8984 10.5596C10.8984 10.5596 10.8985 12.5001 10.8985 13.2002V30.7996C10.8985 31.4998 10.8985 33.4388 10.8985 33.4388C10.8985 33.4388 12.6631 33.4395 13.5455 33.4395H17.9572V43.1192C17.957 43.2934 18.0087 43.4638 18.1056 43.6087C18.2026 43.7536 18.3406 43.8665 18.502 43.9331C18.6633 43.9997 18.8408 44.0171 19.012 43.983C19.1832 43.9489 19.3404 43.865 19.4637 43.7418L40.6397 22.6225C40.7217 22.5408 40.7868 22.4437 40.8312 22.3369C40.8756 22.2301 40.8984 22.1156 40.8984 21.9999C40.8984 21.8843 40.8756 21.7698 40.8312 21.6629C40.7868 21.5561 40.7217 21.4591 40.6397 21.3773ZM19.7218 40.994V32.5595C19.7218 32.3262 19.7218 31.6789 19.7218 31.6789C19.7218 31.6789 19.0735 31.6796 18.8395 31.6796H13.5455C13.3115 31.6796 12.6631 31.6789 12.6631 31.6789C12.6631 31.6789 12.6632 31.033 12.6632 30.7996V13.2002C12.6632 12.9668 12.6631 12.3196 12.6631 12.3196C12.6631 12.3196 13.3115 12.3203 13.5455 12.3203H18.8395C19.0735 12.3203 19.7217 12.3196 19.7217 12.3196C19.7217 12.3196 19.7218 11.6737 19.7218 11.4403V3.00579L38.7669 21.9999L19.7218 40.994Z" fill="#404040"/>
                  <path d="M5.89844 33.429L5.89844 10.5568" stroke="#404040" stroke-width="1.8"/>
                  <path d="M0.898437 33.429L0.898438 10.5568" stroke="#404040" stroke-width="1.8"/>
                </svg>
              </Svg1>
              <h3>Trusted Since 2005</h3>
              <p>Nearly two decades of empowering customers with reliable tools and support.</p>
            </GridItem>
            <GridItem $index={2} $scrollProgress={gridScrollProgress}>
              <Svg1>
                <svg width="30" height="29" viewBox="0 0 30 29" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M29.9106 10.9427C29.808 10.6177 29.6106 10.3311 29.3439 10.1201C29.0772 9.90902 28.7535 9.78322 28.4147 9.75894L19.8768 9.06182L16.5878 1.0686C16.4582 0.752464 16.238 0.482103 15.9551 0.291841C15.6722 0.10158 15.3394 0 14.9988 0C14.6583 0 14.3254 0.10158 14.0425 0.291841C13.7596 0.482103 13.5394 0.752464 13.4099 1.0686L10.1208 9.06182L1.58293 9.75894C1.24364 9.78786 0.920402 9.9167 0.653757 10.1293C0.387112 10.3419 0.188928 10.6287 0.0840552 10.9539C-0.0208174 11.2791 -0.0277095 11.6282 0.0642427 11.9573C0.156195 12.2864 0.342899 12.581 0.600942 12.804L7.10819 18.4381L5.1192 26.858C5.03591 27.1909 5.0536 27.5411 5.17002 27.8638C5.28643 28.1865 5.49626 28.4669 5.77257 28.6691C6.04888 28.8712 6.37906 28.9858 6.72073 28.9983C7.06241 29.0107 7.39999 28.9204 7.69016 28.7388L14.9988 24.2271L22.3075 28.7388C22.5946 28.9209 22.9298 29.0115 23.2692 28.9988C23.6086 28.9861 23.9362 28.8707 24.209 28.6677C24.4895 28.4685 24.7035 28.1888 24.8229 27.8654C24.9424 27.542 24.9617 27.1899 24.8784 26.8552L22.8895 18.4312L29.3967 12.797C29.6575 12.5751 29.8459 12.2797 29.9375 11.9491C30.0292 11.6185 30.0198 11.2679 29.9106 10.9427ZM28.6703 11.9549L21.9089 17.8107C21.8319 17.8774 21.7746 17.964 21.7432 18.0611C21.7119 18.1582 21.7077 18.2621 21.7311 18.3615L23.7965 27.1159C23.8267 27.2356 23.8201 27.3616 23.7775 27.4774C23.7348 27.5932 23.6581 27.6932 23.5576 27.7643C23.4619 27.8359 23.3467 27.8767 23.2274 27.8812C23.1081 27.8857 22.9902 27.8536 22.8895 27.7894L15.2905 23.0977C15.2028 23.0434 15.1018 23.0147 14.9988 23.0147C14.8958 23.0147 14.7948 23.0434 14.7071 23.0977L7.10819 27.7894C7.00742 27.8536 6.88957 27.8857 6.77025 27.8812C6.65094 27.8767 6.5358 27.8359 6.4401 27.7643C6.33954 27.6932 6.26285 27.5932 6.22019 27.4774C6.17753 27.3616 6.17091 27.2356 6.2012 27.1159L8.26657 18.3615C8.28999 18.2621 8.28579 18.1582 8.25444 18.0611C8.22309 17.964 8.16579 17.8774 8.08879 17.8107L1.32736 11.9549C1.23396 11.8758 1.16649 11.7703 1.13377 11.6522C1.10106 11.534 1.10464 11.4087 1.14402 11.2926C1.18024 11.1776 1.25008 11.0763 1.34446 11.0017C1.43883 10.9271 1.55338 10.8827 1.67321 10.8743L10.5542 10.1479C10.6563 10.1398 10.7542 10.1034 10.8371 10.0429C10.92 9.98238 10.9845 9.90002 11.0236 9.80495L14.4432 1.49524C14.488 1.38259 14.5654 1.28602 14.6655 1.21799C14.7655 1.14996 14.8836 1.11359 15.0044 1.11359C15.1252 1.11359 15.2433 1.14996 15.3433 1.21799C15.4433 1.28602 15.5207 1.38259 15.5655 1.49524L18.9851 9.80495C19.0234 9.89845 19.0862 9.97975 19.167 10.0402C19.2477 10.1006 19.3433 10.1378 19.4435 10.1479L28.3189 10.866C28.4387 10.8744 28.5533 10.9187 28.6476 10.9933C28.742 11.0679 28.8119 11.1693 28.8481 11.2842C28.8899 11.4009 28.8954 11.5276 28.8636 11.6475C28.8318 11.7673 28.7644 11.8745 28.6703 11.9549Z" fill="#404040"/>
                </svg>
              </Svg1>
              <h3>Expert Technicians</h3>
              <p>Certified professionals providing top-notch repair and maintenance services.</p>
            </GridItem>
            <GridItem $index={3} $scrollProgress={gridScrollProgress}>
              <Svg1>
              <svg width="18" height="16" viewBox="0 0 18 16" fill="none" xmlns="http://www.w3.org/2000/svg">
                <path d="M17.75 15.25V15.75H0.25V15.25H17.75ZM16.75 0.25V12.9805H15.25V0.25H16.75ZM12.0771 6.25V12.9805H10.5771V6.25H12.0771ZM7.4043 3.25V12.9805H5.9043V3.25H7.4043ZM2.75 8.25V12.9805H1.25V8.25H2.75Z" stroke="#404040" stroke-width="0.5"/>
              </svg>
              </Svg1>
              <h3>Nationwide Recognition</h3>
              <p>Honored as India's leading STIHL dealer, reflecting our excellence.</p>
            </GridItem>
            <GridItem $index={4} $scrollProgress={gridScrollProgress}>
              <Svg1>
                <svg width="30" height="30" viewBox="0 0 30 30" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M29.8167 5.18322L24.8168 0.183301C24.7587 0.125192 24.6898 0.0790934 24.6139 0.0476412C24.5381 0.016189 24.4567 0 24.3746 0C24.2925 0 24.2111 0.016189 24.1353 0.0476412C24.0594 0.0790934 23.9905 0.125192 23.9324 0.183301L8.93267 15.1831C8.87465 15.2411 8.82864 15.3101 8.79727 15.386C8.7659 15.4618 8.74979 15.5431 8.74986 15.6252V20.6252C8.74986 20.7909 8.8157 20.9499 8.93291 21.0671C9.05012 21.1843 9.20909 21.2501 9.37485 21.2501H14.3748C14.4569 21.2502 14.5382 21.2341 14.614 21.2027C14.6899 21.1714 14.7589 21.1254 14.8169 21.0673L29.8167 6.06758C29.8748 6.00954 29.9209 5.94061 29.9524 5.86473C29.9838 5.78886 30 5.70753 30 5.6254C30 5.54327 29.9838 5.46194 29.9524 5.38607C29.9209 5.31019 29.8748 5.24126 29.8167 5.18322ZM14.1154 20.0002H9.99984V15.8846L20.6247 5.25978L24.7402 9.37534L14.1154 20.0002ZM25.6246 8.49098L21.509 4.37542L24.3746 1.50984L28.4902 5.6254L25.6246 8.49098ZM28.7495 15.6252V28.125C28.7495 28.6223 28.552 29.0992 28.2004 29.4508C27.8487 29.8025 27.3718 30 26.8746 30H1.87497C1.3777 30 0.90079 29.8025 0.549166 29.4508C0.197541 29.0992 0 28.6223 0 28.125V3.12544C0 2.62817 0.197541 2.15126 0.549166 1.79964C0.90079 1.44801 1.3777 1.25047 1.87497 1.25047H14.3748C14.5405 1.25047 14.6995 1.31632 14.8167 1.43353C14.9339 1.55073 14.9998 1.7097 14.9998 1.87546C14.9998 2.04122 14.9339 2.20019 14.8167 2.3174C14.6995 2.4346 14.5405 2.50045 14.3748 2.50045H1.87497C1.70921 2.50045 1.55024 2.5663 1.43303 2.68351C1.31583 2.80071 1.24998 2.95968 1.24998 3.12544V28.125C1.24998 28.2908 1.31583 28.4498 1.43303 28.567C1.55024 28.6842 1.70921 28.75 1.87497 28.75H26.8746C27.0403 28.75 27.1993 28.6842 27.3165 28.567C27.4337 28.4498 27.4995 28.2908 27.4995 28.125V15.6252C27.4995 15.4595 27.5654 15.3005 27.6826 15.1833C27.7998 15.0661 27.9588 15.0002 28.1245 15.0002C28.2903 15.0002 28.4493 15.0661 28.5665 15.1833C28.6837 15.3005 28.7495 15.4595 28.7495 15.6252Z" fill="#404040"/>
                </svg>
              </Svg1>
              <h3>Comprehensive Support</h3>
              <p>From sales to SMAM subsidies and after-sales service, we've got you covered.</p>
            </GridItem>
            <GridItem $index={5} $scrollProgress={gridScrollProgress}>
              <Svg1>
                <svg viewBox="0 0 49 48" fill="none" xmlns="http://www.w3.org/2000/svg">
                  <path d="M25.5 35c7.732 0 14-6.268 14-14s-6.268-14-14-14-14 6.268-14 14 6.268 14 14 14" stroke="#404040" stroke-linecap="round" stroke-width="2" stroke-linejoin="round"/>
                  <path d="M4.5 44c5.015-5.501 12.095-9 20.5-9s15.485 3.499 20.5 9" stroke="#404040" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"/>
                  <path d="M26.125 35h-2.25l-2.728 2.645a1 1 0 0 0-.09 1.336L25 44l3.943-5.019a1 1 0 0 0-.09-1.336z" stroke="#404040" stroke-width="2"/>
                </svg>
              </Svg1>
              <h3>Local Accessibility</h3>
              <p>Easily accessible branches in Kochi, Thiruvananthapuram, Thrissur, and more.</p>
            </GridItem>
          </SixGridContainer>
        </div>
      </GridSection>

      <DiscoverySection>
        <div className="container">
          <SectionTitle>Our Values</SectionTitle>
          <DiscoveryGrid>
            <DiscoveryImageContainer>
              <img 
                src={discoveryData[hoveredDiscovery !== null ? hoveredDiscovery : activeDiscovery].image} 
                alt={discoveryData[hoveredDiscovery !== null ? hoveredDiscovery : activeDiscovery].title}
              />
            </DiscoveryImageContainer>
            <DiscoveryCardsContainer>
              {discoveryData.map((item, index) => (
                <DiscoveryCard
                  key={index}
                  className={`${activeDiscovery === index ? 'active' : ''} ${hoveredDiscovery !== null && hoveredDiscovery !== index ? 'disabled' : ''}`}
                  onMouseEnter={() => {
                    setHoveredDiscovery(index);
                    setActiveDiscovery(index);
                  }}
                  onMouseLeave={() => setHoveredDiscovery(null)}
                >
                  <h3>{item.title}</h3>
                  <p>{item.description}</p>
                </DiscoveryCard>
              ))}
            </DiscoveryCardsContainer>
          </DiscoveryGrid>
          <DiscoveryCarousel
            ref={carouselRef}
            onMouseDown={handleDragStart}
            onMouseMove={handleDragMove}
            onMouseUp={handleDragEnd}
            onMouseLeave={handleDragEnd}
            onTouchStart={handleDragStart}
            onTouchMove={handleDragMove}
            onTouchEnd={handleDragEnd}
          >
            {discoveryData.map((item, index) => (
              <CarouselItem key={index} $isActive={activeDiscovery === index}>
                <CarouselImage className={isAnimating ? 'animate' : ''}>
                  <img src={item.image} alt={item.title} />
                </CarouselImage>
                <CarouselContent className="carousel-content">
                  <CarouselTextContent $isActive={activeDiscovery === index}>
                    <h3>{item.title}</h3>
                    <p>{item.description}</p>
                  </CarouselTextContent>
                  <CarouselIndicator>{`${activeDiscovery + 1}/${discoveryData.length}`}</CarouselIndicator>
                </CarouselContent>
                <CarouselDots>
                  {discoveryData.map((_, index) => (
                    <CarouselDot
                      key={index}
                      $isActive={activeDiscovery === index}
                      onClick={() => setActiveDiscovery(index)}
                    />
                  ))}
                </CarouselDots>
              </CarouselItem>
            ))}
          </DiscoveryCarousel>
        </div>
      </DiscoverySection>

      <CTASection>
        <div className="container">
          <h2>Ready to Power Your Projects?</h2>
          <p>
            Discover our complete range of innovative tools and accessories.
          </p>
          <CTAButton to="/collections/all-categories">Shop Our Products</CTAButton>
        </div>
      </CTASection>
    </AboutPageWrapper>
  );
};

export default AboutPage;
